import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Spinner, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import IndicadorCard from "../../components/IndicadorCard";
import SelectorCuenta, { TOTAL_CUENTA_ID } from "../../components/SelectorCuenta";
import IngresosPanel from "./IngresosPanel";
import GastosEsporadicosPanel from "./GastosEsporadicosPanel";
import useCuentasCliente from "../../hooks/useCuentasCliente";
import { useAuth } from "../../auth/AuthContext";
import * as dashboardApi from "../../api/dashboard";
import * as cuentasApi from "../../api/cuentas";
import * as reportesApi from "../../api/reportes";
import * as gastosFijosApi from "../../api/gastosFijos";
import { diasHastaVencer } from "../../utils/gastosFijos";

const CAMPOS_SUMABLES = ["saldoActual", "ingresosMes", "gastosFijosPendientes", "gastosFijosPagadosMes", "gastosEsporadicosMes", "egresosMes", "saldoDisponibleReal"];

function sumarResumenes(resumenes) {
  return resumenes.reduce((acc, r) => {
    CAMPOS_SUMABLES.forEach((campo) => { acc[campo] = (acc[campo] || 0) + r[campo]; });
    return acc;
  }, {});
}

/** Todos los gastos fijos pendientes a 2 días o menos de su fecha de cobro, del más próximo al más lejano. */
function proximosAVencer(gastos) {
  return gastos
    .filter((g) => g.estado === "Pendiente" && diasHastaVencer(g) <= 2)
    .sort((a, b) => diasHastaVencer(a) - diasHastaVencer(b))
    .map((g) => ({ motivo: g.motivo, monto: g.monto }));
}

export default function ClienteDashboard() {
  const { monedaPredeterminada } = useAuth();
  const navigate = useNavigate();
  const { cuentas, cuentaId, setCuentaId, cargando: cargandoCuentas, recargar } = useCuentasCliente();
  const [resumen, setResumen] = useState(null);
  const [proximosVencer, setProximosVencer] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [showSaldoInicial, setShowSaldoInicial] = useState(false);
  const esTotal = cuentaId === TOTAL_CUENTA_ID;
  const cuentaActual = !esTotal ? cuentas.find((c) => c.cuentaId === cuentaId) : null;
  const esTarjeta = cuentaActual?.tipo === "TarjetaCredito";
  const hoy = new Date();

  const cargarResumen = async () => {
    if (!cuentaId) return;
    setCargando(true);
    try {
      if (esTotal) {
        // El total combinado excluye Tarjetas de Crédito (su saldo es deuda, no efectivo disponible).
        const cuentasBancarias = cuentas.filter((c) => c.tipo !== "TarjetaCredito");
        const [resultados, listas] = await Promise.all([
          Promise.all(cuentasBancarias.map((c) => dashboardApi.dashboardCliente(c.cuentaId))),
          Promise.all(cuentasBancarias.map((c) => gastosFijosApi.listarGastosFijos(c.cuentaId, hoy.getMonth() + 1, hoy.getFullYear()))),
        ]);
        setResumen(sumarResumenes(resultados));
        setProximosVencer(proximosAVencer(listas.flat()));
      } else {
        const [data, gastos] = await Promise.all([
          dashboardApi.dashboardCliente(cuentaId),
          gastosFijosApi.listarGastosFijos(cuentaId, hoy.getMonth() + 1, hoy.getFullYear()),
        ]);
        setResumen(data);
        setProximosVencer(proximosAVencer(gastos));
      }
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarResumen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cuentaId]);

  /** Tras un alta/edición rápida que cambia el saldo, refresca el resumen y (silenciosamente) el
   * selector de cuentas, para que su saldo mostrado no quede desactualizado hasta recargar la página. */
  const onSaldoCambiado = () => {
    cargarResumen();
    recargar(false);
  };

  if (cargandoCuentas) return <Spinner animation="border" />;

  if (!cuentas.length) {
    return (
      <Alert variant="info">
        Aún no tienes ninguna cuenta bancaria registrada. Ve a <strong>Cuentas Bancarias</strong> para crear la primera.
      </Alert>
    );
  }

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <h4 className="mb-0">Mi Dashboard</h4>
        {!esTotal && (
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-primary" onClick={() => setShowSaldoInicial(true)}>
              <i className="bi bi-pencil-square me-1"></i>Fijar saldo actual
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={() => reportesApi.descargarResumenMensualPdf(cuentaId, hoy.getMonth() + 1, hoy.getFullYear())}
            >
              <i className="bi bi-file-earmark-pdf me-1"></i>Imprimir resumen
            </Button>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => reportesApi.descargarAnualPdf(cuentaId, hoy.getFullYear())}
            >
              <i className="bi bi-calendar3 me-1"></i>Reporte anual
            </Button>
          </div>
        )}
      </div>

      <SelectorCuenta cuentas={cuentas} cuentaId={cuentaId} onChange={setCuentaId} />

      {cargando || !resumen ? (
        <Spinner animation="border" />
      ) : (
        <>
          {esTarjeta ? (
            <div className="fs-card border-2 mb-3" style={{ borderColor: "var(--fs-ingreso)" }}>
              <div className="fs-card-label">Disponible (línea de crédito − usado − gastos fijos pendientes)</div>
              <div className="fs-card-value text-fs-ingreso" style={{ fontSize: "2.1rem" }}>
                {(Number(cuentaActual.limiteCredito || 0) - resumen.saldoActual - resumen.gastosFijosPendientes)
                  .toLocaleString("es-ES", { style: "currency", currency: monedaPredeterminada.codigo })}
              </div>
            </div>
          ) : (
            <div className="fs-card border-2 mb-3" style={{ borderColor: "var(--fs-primary)" }}>
              <div className="fs-card-label">Saldo disponible real (saldo actual − gastos fijos pendientes)</div>
              <div className="fs-card-value" style={{ fontSize: "2.1rem", color: "var(--fs-primary)" }}>
                {resumen.saldoDisponibleReal.toLocaleString("es-ES", { style: "currency", currency: monedaPredeterminada.codigo })}
              </div>
            </div>
          )}

          <div className="row g-3 mb-3">
            <div className="col-6 col-md-4">
              <IndicadorCard
                label={esTarjeta ? "Monto usado" : "Saldo actual"}
                valor={resumen.saldoActual}
                tono={esTarjeta ? "gasto" : "neutral"}
                icono="bi-wallet2"
                onClick={() => navigate(esTarjeta ? "/tarjetas-credito" : "/cuentas")}
              />
            </div>
            <div className="col-6 col-md-4">
              <IndicadorCard label="Ingresos del mes" valor={resumen.ingresosMes} tono="ingreso" icono="bi-arrow-down-circle" onClick={() => navigate("/ingresos")} />
            </div>
            <div className="col-6 col-md-4">
              <IndicadorCard
                label="Gastos fijos pendientes"
                valor={resumen.gastosFijosPendientes}
                tono="alerta"
                icono="bi-hourglass-split"
                alertaProximosAVencer={proximosVencer}
                onClick={() => navigate("/gastos?tab=fijos")}
              />
            </div>
            <div className="col-6 col-md-4">
              <IndicadorCard label="Gastos fijos pagados del mes" valor={resumen.gastosFijosPagadosMes} tono="gasto" icono="bi-receipt" onClick={() => navigate("/gastos?tab=fijos")} />
            </div>
            <div className="col-6 col-md-4">
              <IndicadorCard label="Gastos + Egresos del mes" valor={resumen.gastosEsporadicosMes + resumen.egresosMes} tono="gasto" icono="bi-arrow-up-circle" onClick={() => navigate("/gastos?tab=esporadicos")} />
            </div>
          </div>

          {!esTotal && !esTarjeta && <IngresosPanel cuentaId={cuentaId} onCambio={onSaldoCambiado} />}
          {!esTotal && <GastosEsporadicosPanel cuentaId={cuentaId} onCambio={onSaldoCambiado} />}
        </>
      )}

      <ModalSaldoInicial
        show={showSaldoInicial}
        onHide={() => setShowSaldoInicial(false)}
        cuentaId={cuentaId}
        onGuardado={() => {
          setShowSaldoInicial(false);
          onSaldoCambiado();
        }}
      />
    </div>
  );
}

function ModalSaldoInicial({ show, onHide, cuentaId, onGuardado }) {
  const [saldo, setSaldo] = useState("");
  const [guardando, setGuardando] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await cuentasApi.fijarSaldoInicial(cuentaId, Number(saldo));
      onGuardado();
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>Fijar saldo actual</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Nuevo saldo actual de la cuenta</Form.Label>
            <Form.Control type="number" step="0.01" value={saldo} onChange={(e) => setSaldo(e.target.value)} required autoFocus />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={guardando}>
            {guardando ? <Spinner size="sm" animation="border" /> : "Guardar"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
